import { useEffect, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { fmtDate, statusClass } from "../lib/utils";
import { Plus, ChatCircleDots, ArrowLeft, PaperPlaneTilt, Headset, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Modal, Field } from "./AdminClients";
import PageHeader from "../components/PageHeader";

export default function ClientSupport() {
  const { t, lang } = useI18n();
  const [tickets, setTickets] = useState([]);
  const [creating, setCreating] = useState(false);
  const [active, setActive] = useState(null);

  const load = async () => {
    const { data } = await api.get("/client/tickets");
    setTickets(data);
  };
  useEffect(() => { load(); }, []);

  const openTicket = async (id) => {
    const { data } = await api.get(`/client/tickets/${id}`);
    setActive(data);
  };

  if (active) return <TicketView ticket={active} onClose={() => { setActive(null); load(); }} role="client" />;

  return (
    <div className="space-y-6" data-testid="client-support">
      <PageHeader
        eyebrow={t("support")}
        title={t("your_tickets")}
        subtitle={t("support_page_subtitle")}
        chip="tf-chip-sky"
        icon={Headset}
        actions={
          <button onClick={() => setCreating(true)} className="tf-btn tf-btn-primary" data-testid="new-ticket-btn">
            <Plus size={16} weight="bold" /> {t("new_ticket")}
          </button>
        }
      />

      {/* Calendly quick-call - minimal outlined */}
      <a
        href="https://calendly.com/office-tee/30min"
        target="_blank"
        rel="noopener noreferrer"
        className="tf-card p-4 md:p-5 flex items-center gap-4 hover:border-[var(--tf-ink)] transition-colors group"
        data-testid="support-calendly-card"
      >
        <div className="w-12 h-12 rounded-2xl bg-[#DAECFA] text-[#1F5A93] flex items-center justify-center flex-shrink-0">
          <Headset size={22} weight="fill" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-sm md:text-base">{t("support_book_call")}</div>
          <div className="text-xs text-[var(--tf-muted)] mt-0.5">{t("support_book_15min")}</div>
        </div>
        <span
          className="inline-flex items-center gap-1.5 text-xs font-bold text-[var(--tf-ink)] group-hover:gap-2 transition-all flex-shrink-0"
          data-testid="support-calendly-cta"
        >
          {t("support_book")} <ArrowSquareOut size={13} weight="bold" />
        </span>
      </a>

      {/* Tickets list - card style, no scroll */}
      <div className="tf-card overflow-hidden">
        <div className="p-4 md:p-5 border-b border-[var(--tf-border)] flex items-center justify-between">
          <div className="font-display font-bold text-base md:text-lg">{t("support_open_tickets")}</div>
          <span className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold tf-numeric">{tickets.length}</span>
        </div>
        <div className="divide-y divide-[var(--tf-border)]">
          {tickets.length === 0 && (
            <div className="text-center py-12">
              <div className="w-14 h-14 rounded-2xl bg-[#F5F2E8] text-[var(--tf-ink)] flex items-center justify-center mx-auto mb-3">
                <ChatCircleDots size={24} weight="fill" />
              </div>
              <div className="font-display font-bold text-sm">{t("support_no_open_tickets")}</div>
              <div className="text-xs text-[var(--tf-muted)] mt-1">{t("no_tickets")}</div>
            </div>
          )}
          {tickets.map((tk) => {
            const priorityColor = tk.priority === "high" ? { bg: "#FBDDE1", fg: "#A11534" }
              : tk.priority === "low" ? { bg: "#F5F2E8", fg: "#193529" }
              : { bg: "#DAECFA", fg: "#1F5A93" };
            return (
              <button
                key={tk.id}
                onClick={() => openTicket(tk.id)}
                className="w-full flex items-center gap-3 md:gap-4 p-4 md:px-5 text-left hover:bg-[var(--tf-bg)]/40 transition-colors"
                data-testid={`ticket-row-${tk.id}`}
              >
                <div
                  className="w-11 h-11 md:w-12 md:h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ background: priorityColor.bg, color: priorityColor.fg }}
                >
                  <ChatCircleDots size={20} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm truncate">{tk.subject}</div>
                  <div className="text-[11px] text-[var(--tf-muted)] mt-0.5 truncate">
                    {fmtDate(tk.updated_at, lang)}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className={`tf-status-pill ${statusClass(tk.status === "open" ? "pending" : tk.status === "in_progress" ? "info" : tk.status === "resolved" ? "active" : "neutral")} text-[9px]`}>
                    {t(tk.status)}
                  </span>
                  <span className="text-[9px] uppercase tracking-widest text-[var(--tf-muted)] font-bold">{t(tk.priority)}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {creating && <NewTicketModal onClose={() => setCreating(false)} onCreated={(t) => { setCreating(false); load(); setActive(t); }} />}
    </div>
  );
}

function NewTicketModal({ onClose, onCreated }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ subject: "", body: "", priority: "normal" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.body.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/client/tickets", form);
      toast.success(t("support_ticket_created"));
      onCreated(data);
    } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
    finally { setLoading(false); }
  };
  return (
    <Modal onClose={onClose}>
      <form onSubmit={submit} className="p-6 space-y-4" data-testid="new-ticket-modal">
        <h3 className="font-display font-bold text-2xl">{t("new_ticket")}</h3>
        <Field label={t("subject")} value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} required testid="ticket-subject" />
        <div>
          <label className="tf-label block mb-2">{t("priority")}</label>
          <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="tf-input" data-testid="ticket-priority">
            <option value="low">{t("low")}</option>
            <option value="normal">{t("normal")}</option>
            <option value="high">{t("high")}</option>
          </select>
        </div>
        <div>
          <label className="tf-label block mb-2">{t("message")}</label>
          <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={6} className="tf-input" required data-testid="ticket-body" />
        </div>
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="tf-btn tf-btn-ghost flex-1">{t("cancel")}</button>
          <button type="submit" disabled={loading} className="tf-btn tf-btn-primary flex-1" data-testid="ticket-submit">
            <PaperPlaneTilt size={14} weight="bold" /> {t("open_ticket")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function TicketView({ ticket: initial, onClose, role }) {
  const { t, lang } = useI18n();
  const [ticket, setTicket] = useState(initial);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const path = role === "admin" ? `/admin/tickets/${ticket.id}/reply` : `/client/tickets/${ticket.id}/reply`;
      const { data } = await api.post(path, { body: reply });
      setTicket(data);
      setReply("");
    } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
    finally { setSending(false); }
  };

  const setStatus = async (status) => {
    if (role !== "admin") return;
    await api.patch(`/admin/tickets/${ticket.id}/status`, { status });
    setTicket({ ...ticket, status });
    toast.success(t("support_status_updated"));
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="ticket-view">
      <button onClick={onClose} className="tf-btn tf-btn-ghost"><ArrowLeft size={14} weight="bold" /> {t("back")}</button>
      <div className="tf-card p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="tf-label">{t("subject")}</div>
            <h2 className="font-display font-bold text-2xl mt-1">{ticket.subject}</h2>
            {role === "admin" && ticket.client && (
              <div className="text-sm text-[var(--tf-muted)] mt-1">{ticket.client.company_name} · {ticket.client_email}</div>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className={`tf-status-pill ${statusClass(ticket.status === "open" ? "pending" : ticket.status === "resolved" ? "active" : "neutral")}`}>{t(ticket.status)}</span>
            <span className={`tf-status-pill ${ticket.priority === "high" ? "tf-status-error" : "tf-status-neutral"}`}>{t(ticket.priority)}</span>
          </div>
        </div>
        {role === "admin" && (
          <div className="flex gap-2 mt-4">
            <button onClick={() => setStatus("in_progress")} className="tf-btn tf-btn-ghost text-xs">{t("in_progress")}</button>
            <button onClick={() => setStatus("resolved")} className="tf-btn tf-btn-ghost text-xs">{t("mark_resolved")}</button>
            <button onClick={() => setStatus("closed")} className="tf-btn tf-btn-ghost text-xs">{t("closed")}</button>
          </div>
        )}
      </div>

      <div className="space-y-4">
        {ticket.messages?.map((m) => {
          const isMine = (role === "admin" && m.sender_role === "admin") || (role === "client" && m.sender_role === "client");
          return (
            <div key={m.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] tf-card p-4 ${m.sender_role === "admin" ? "bg-[var(--tf-ink)] text-white border-[var(--tf-ink)]" : ""}`}>
                <div className={`tf-label mb-2 ${m.sender_role === "admin" ? "!text-white/60" : ""}`}>
                  {m.sender_role === "admin" ? t("support_teefarm") : t("support_you")} · {fmtDate(m.created_at, lang)}
                </div>
                <div className={`whitespace-pre-wrap text-sm ${m.sender_role === "admin" ? "text-white" : ""}`}>{m.body}</div>
              </div>
            </div>
          );
        })}
      </div>

      {ticket.status !== "closed" && (
        <div className="tf-card p-4">
          <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={4} placeholder={t("reply_placeholder")} className="tf-input" data-testid="ticket-reply-input" />
          <div className="flex justify-end mt-3">
            <button onClick={send} disabled={sending || !reply.trim()} className="tf-btn tf-btn-primary" data-testid="ticket-reply-send">
              <PaperPlaneTilt size={14} weight="bold" /> {t("send_reply")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
